import { useEffect, useState } from "react";
import AppLayout from "../../components/Layouts/AppLayout";
import { useStateContext } from "../../context/ContextProvider";
import productionEmployeesAPI from "../../services/api/ProductionEmployees/productionEmployeesAPI";
import { deleteZero, handleInteger, handleNumber, handleZero, isValid, numericSortComparator } from "../../helpers/helper";
import Loading from "../../components/Loading";
import dayjs from "dayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AUTOCOMPLETE_STYLE, MOBILE_DATE_PICKER_STYLE, SELECT_STYLE, TEXTFIELD_STYLE } from "../../constants/constants";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableShared from "../../components/TableShared";
import { Autocomplete, Box, FormControl, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import AddButton from "../../components/AddButton";
import { useDebounce } from "use-debounce";
import { cloneDeep, isEqual, round } from "lodash";
import hourlyRatesAPI from "../../services/api/HourlyRates/hourlyRatesAPI";
import Swal from "sweetalert2";
import { useSessionStorage } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";
import usePrompt from "../../hooks/usePrompt";

const HourlyRates = () => {
    const { t } = useTranslation()

    const { choosesite, config, currencySymbol } = useStateContext()

    const [isLoading, setIsLoading] = useState(true);

    const [storedStartDate, setStoredStartDate] = useSessionStorage('hourlyRates.startDate', dayjs());
    const [storedEndDate, setStoredEndDate] = useSessionStorage('hourlyRates.endDate', dayjs());
    const [startDate, setStartDate] = useState(dayjs(storedStartDate));
    const [endDate, setEndDate] = useState(dayjs(storedEndDate));
    const [startDatePickerDate, setStartDatePickerDate] = useState(dayjs(storedStartDate));
    const [endDatePickerDate, setEndDatePickerDate] = useState(dayjs(storedEndDate));

    const [productionEmployees, setProductionEmployees] = useState([]);
    const [count, setCount] = useState(10000);
    const [hourlyRates, setHourlyRates] = useState([]);
    const [initialHourlyRates, setInitialHourlyRates] = useState([]);

    const [isDirty, setIsDirty] = useState(false);

    {/** EFFECTS */}

    useEffect(() => {
        if(isValid(choosesite)) {
            getProductionEmployees(choosesite);
        }
    }, [choosesite])

    useEffect(() => {
        if (!startDate.isSame(endDate) && startDate.isAfter(endDate)) {
            setStoredEndDate(startDate);
            setEndDate(startDate);
            setEndDatePickerDate(startDate);
        }
    }, [startDate, endDate])

    useEffect(() => {
        if(isValid(choosesite)) {
            getHourlyRates(choosesite, startDate, endDate);
        }
    }, [choosesite, startDate, endDate])

    useEffect(() => {
        if(isEqual(hourlyRates, initialHourlyRates)) {
            setIsDirty(false);
        } else {
            setIsDirty(true);
        }
    }, [hourlyRates, initialHourlyRates])

    {/** GETTERS */}

    const getHourlyRates = async (_siteId, _startDate, _endDate) => {
        setIsLoading(true);

        await hourlyRatesAPI.getHourlyRates(config, {
            start_date: dayjs(_startDate).format('YYYY-MM-DD'),
            end_date: dayjs(_endDate).format('YYYY-MM-DD'),
            site_id: _siteId
        })
            .then((data) => {
                setHourlyRates(data);
                setInitialHourlyRates(cloneDeep(data));
            })
            .finally(() => setIsLoading(false));
    }

    const saveHourlyRates = async () => {
        if(hourlyRates.some((hr) => hr.employee_id == '')) {
            Swal.fire({
                icon: "error",
                customClass: 'error',
                showCloseButton: true,
                iconColor: '#FF0000',
                text: t('please_select_a_production_employee')
            });
            return;
        }

        setIsLoading(true);

        await hourlyRatesAPI.saveHourlyRates(config, {
            start_date: dayjs(startDate).format('YYYY-MM-DD'),
            end_date: dayjs(endDate).format('YYYY-MM-DD'),
            site_id: choosesite,
            hourly_rates: hourlyRates
        })
            .then((data) => {
                if(data.status) {
                    Swal.fire({
                        icon: "success",
                        customClass: 'success',
                        showCloseButton: true,
                        iconColor: '#00B78E',
                        text: data.message
                    });
                    setInitialHourlyRates(cloneDeep(hourlyRates));
                } else {
                     Swal.fire({
                        icon: "error",
                        customClass: 'error',
                        showCloseButton: true,
                        iconColor: '#FF0000',
                        text: data.message
                    });
                }
            })
            .finally(() => setIsLoading(false));
    }

    const getProductionEmployees = async (siteId) => {
        setIsLoading(true);

        await productionEmployeesAPI.getProductionEmployees(
            config, {
                where: {
                    site: siteId,
                    payment_method: 'hourly_rate'
                },
                with: ['defaultHourlyRates']
            }
        )
            .then((data) => setProductionEmployees(data))
            .finally(() => setIsLoading(false));
    }

    {/** MISC. */}

    const handleSelectEmployee = (id, _employee) => {
        const hourlyRatesCopy = [...hourlyRates]
        const hourlyRate = hourlyRatesCopy.find((hr) => hr.id === id);

        hourlyRate.employee_id = _employee.id;
        hourlyRate.employee_name = _employee.name;
        hourlyRate.default_hourly_rate = _employee.default_hourly_rate;
        hourlyRate.hourly_rate = hourlyRate.default_hourly_rate;

        setHourlyRates(hourlyRatesCopy);
    }

    const handleChangeHoursWorked = (id, _hoursWorked) => {
        const hourlyRatesCopy = [...hourlyRates]
        const hourlyRate = hourlyRatesCopy.find((hr) => hr.id === id);

        hourlyRate.hours_worked = Number(_hoursWorked);

        setHourlyRates(hourlyRatesCopy);
    }

    const handleChangeHourlyRate = (id, _hourlyRate) => {
        const hourlyRatesCopy = [...hourlyRates]
        const hourlyRate = hourlyRatesCopy.find((hr) => hr.id === id);

        hourlyRate.hourly_rate = Number(_hourlyRate);

        setHourlyRates(hourlyRatesCopy);
    }

    const handleAddHourlyRate = (e) => {
        e.preventDefault();

        if(e.detail > 0) {
            const hourlyRate = {
                "id": count,
                'employee_id': '',
                "employee_name": '',
                "default_hourly_rate": '',
                'hours_worked': '',
                'hourly_rate': '',
                'total': ''
            };

            setHourlyRates((prev) => [...prev, hourlyRate]);
            setCount((prev) => prev + 1);
        }
    }

    const handleAcceptDate = async (date, type) => {
        if(isDirty) {
            const isConfirm = await Swal.fire({
                title: t('caution'),
                text: t('unsaved_changes_warning'),
                icon: 'warning',
                customClass: 'error',
                showCloseButton: true,
                iconColor: '#FF0000',
                reverseButtons: true,
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: t('confirm'),
                cancelButtonText: t('cancel')
            }).then((result) => {
                return result.isConfirmed
            })

            if (!isConfirm) {
                setStartDatePickerDate(startDate);
                setEndDatePickerDate(endDate);
                return;
            }
        }

        switch(type) {
            case "start":
                setStoredStartDate(date);
                setStartDate(date);
                setStartDatePickerDate(date);
                break;
            case "end":
                setStoredEndDate(date);
                setEndDate(date);
                setEndDatePickerDate(date);
                break;
            default:
        }
    }

    const columns = [
        {
            field: 'employee_name',
            headerName: t('employee'),
            flex: 1,
            minWidth: 210,
            renderCell: (params) => (
                <EmployeeSelectCell
                    params={params}
                    productionEmployees={productionEmployees}
                    handleSelectEmployee={handleSelectEmployee}
                    hourlyRates={hourlyRates}
                />
            )
        },
        {
            field: 'hours_worked',
            headerName: t('hours_worked'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => (
                <HoursWorkedCell
                    params={params}
                    handleChangeHoursWorked={handleChangeHoursWorked}
                    productionEmployees={productionEmployees}
                />
            )
        },
        {
            field: 'hourly_rate',
            headerName: t('hourly_rate'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => (
                <HourlyRateCell
                    params={params}
                    handleChangeHourlyRate={handleChangeHourlyRate}
                    currencySymbol={currencySymbol}
                    productionEmployees={productionEmployees}
                />
            )
        },
        {
            field: 'total',
            headerName: t('total'),
            flex: 1,
            minWidth: 210,
            renderCell: (params) => (
                <TotalCell
                    params={params}
                    currencySymbol={currencySymbol}
                />
            ),
            valueGetter: (params) => {
                try {
                    return params.row.hourly_rate * params.row.hours_worked;
                } catch (e) {
                    return 0;
                }
            },
            sortComparator: numericSortComparator
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            cellClassName: 'padding-0',
            flex: 0.5,
            minWidth: 115,
            renderCell: (params) => (
                <ActionsCell
                    params={params}
                    hourlyRates={hourlyRates}
                    setHourlyRates={setHourlyRates}
                    setProductionEmployees={setProductionEmployees}
                />
            )

        }
    ];

    usePrompt(
        isDirty,
        t('unsaved_changes_warning')
    );

    return (
        <>
            {isLoading && <Loading />}
            <AppLayout>
                <div className='p-5 flex justify-between gap-3 flex-col lg:flex-row'>
                    <div className='bg-white rounded-md w-full h-fit'>
                        <div className='border-b p-5'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('select_x', {x: t('date')})}</p>
                        </div>
                        <div className='p-5 flex gap-3 justify-between max-[500px]:flex-col'>
                            <div className='w-full lg:w-1/2 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('start_date')}</p>
                                <div className='w-full'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            id="select-start-date"
                                            sx={MOBILE_DATE_PICKER_STYLE}
                                            format="YYYY-MM-DD"
                                            /* disabled={disabled} */
                                            value={startDatePickerDate}
                                            onChange={(newValue) => setStartDatePickerDate(newValue)}
                                            onAccept={(newValue) => handleAcceptDate(newValue, "start")}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                            <div className='w-full lg:w-1/2 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('end_date')}</p>
                                <div className='w-full'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            id="select-end-date"
                                            sx={MOBILE_DATE_PICKER_STYLE}
                                            format="YYYY-MM-DD"
                                            /* disabled={disabled} */
                                            value={endDatePickerDate}
                                            minDate={startDate}
                                            onChange={(newValue) => setEndDatePickerDate(newValue)}
                                            onAccept={(newValue) => handleAcceptDate(newValue, "end")}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex flex-col gap-5 p-5 pt-0'>
                    <div className='pb-5 bg-white mb-2 rounded-md flex items-start flex-col w-full'>
                        <div className='flex justify-between items-center p-5 border-b w-full'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>
                                {t('enter_hours_and_rates')}
                                <span style={{visibility: isDirty ? 'visible' : 'hidden'}}>*</span>
                            </p>
                            <Tooltip
                                disableInteractive
                                title={
                                    productionEmployees.length < 1
                                        ? t('_hourly_rate.require_production_employee')
                                        : t('add_new_x', { x: t('hourly_rate')})
                                }
                                placement='left'
                            >
                                <span>
                                    <AddButton
                                        onClick={(e) => handleAddHourlyRate(e)}
                                        disabled={productionEmployees.length < 1}
                                    >
                                        <span style={{ paddingLeft: '2px' }}>
                                            <i className="fa-solid fa-plus flex justify-center"></i>
                                        </span>
                                    </AddButton>
                                </span>
                            </Tooltip>
                        </div>
                        <Box width='100%'>
                            <TableShared
                                columns={columns}
                                items={hourlyRates}
                                oddColumnPadding='20px'
                                flat={true}
                                headerRowFontSize='15px'
                                columnBuffer={columns.length}
                            />
                            <div className='p-5 pb-0 flex gap-3 justify-end'>
                                <button
                                    type="button"
                                    onClick={saveHourlyRates}
                                    className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#00C448] hover:bg-[#019738] disabled:bg-[#B8B7BC]'
                                >
                                    <span className='pr-2'><i className="fa-solid fa-check"></i></span> {t('save')}
                                </button>
                            </div>
                        </Box>
                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default HourlyRates;

const EmployeeSelectCell = ({
    params,
    productionEmployees,
    handleSelectEmployee,
    hourlyRates
}) => {
    const { t } = useTranslation();

    const { row } = params;

    const [keys] = useState({
        menuItem: 'name',
        textField: 'name'
    });
    const [employee, setEmployee] = useState(null);
    const [employeeInputValue, setEmployeeInputValue] = useState('');
    const [exists, setExists] = useState(false);

    const handleChange = (e) => {
        setEmployee((prev) => {
            if (prev == null) {
                return prev;
            }

            return prev;
        });
        setEmployee(e);
        handleSelectEmployee(row.id, e);
        /* setEmployee(e.target.value);
        handleSelectEmployee(row.id, e.target.value); */
    }

    useEffect(() => {
        if (productionEmployees.length > 0) {
            if(row.employee_id !== '') {
                const target = productionEmployees?.find((pe) => pe.id === row.employee_id);

                if(target) {
                    setEmployee(target);
                    setExists(true);
                } else {
                    setEmployee(null);
                    setExists(false);
                }
            } else {
                setExists(true);
            }
        }
    }, [productionEmployees])

    return exists ? (
        <FormControl variant="standard" sx={{ width: '100%' }}>
            <Autocomplete
                disableClearable
                id="production-employee-autocomplete"
                options={
                    productionEmployees?.map((pe) => ({
                        id: pe.id,
                        label: pe[keys.menuItem],
                        name: pe.name,
                        employee_id: pe.id,
                        employee_name: pe.name,
                        default_hourly_rate: pe.default_hourly_rates?.length > 0 ? pe.default_hourly_rates[0].hourly_rate : 0
                    }))
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionDisabled={(option) => hourlyRates.some((hr) => hr.employee_id == option.id)}
                getOptionLabel={(option) => option.name }
                value={employee}
                onChange={(a, b) => handleChange(b) }
                inputValue={employeeInputValue}
                onInputChange={(e, value) => setEmployeeInputValue(value)}
                renderInput={(params) => <TextField {...params} variant="standard" />}
            />
        </FormControl>
    ) : (
        <TextField
            type="text"
            variant='standard'
            disabled
            className='w-full mb-5 px-0 pt-0'
            value={row?.employee_name}
        />
    )
}

const HoursWorkedCell = ({
    params,
    handleChangeHoursWorked,
    productionEmployees
}) => {
    const { row, value } = params;

    const [hoursWorked, setHoursWorked] = useState(value);
    const [debouncedHoursWorked] = useDebounce(hoursWorked, 250);
    const [exists, setExists] = useState(false);

    useEffect(() => {
        setHoursWorked(value);
    }, [value])

    useEffect(() => {
        if(debouncedHoursWorked && isFinite(debouncedHoursWorked)) {
            handleChangeHoursWorked(row.id, debouncedHoursWorked);
        } else {
            handleChangeHoursWorked(row.id, 0);
        }
    }, [debouncedHoursWorked])

    useEffect(() => {
        if (productionEmployees.length >= 0) {
            if(row.employee_id !== '') {
                const target = productionEmployees?.find((pe) => pe.id === row.employee_id);

                if(target) {
                    setExists(true);
                } else {
                    setExists(false);
                }
            } else {
                setExists(true);
            }
        }
    }, [productionEmployees])

    return (
        <TextField
            type="text"
            variant="standard"
            className='w-full mb-5 px-0 pt-0'
            value={hoursWorked}
            onChange={(e) => handleNumber({
                event: e,
                setter: setHoursWorked,
                precision: 2
            })}
            onBlur={(e) => handleZero({
                event: e,
                setter: setHoursWorked
            })}
            onFocus={(e) => deleteZero({
                event: e,
                setter: setHoursWorked,
                value: hoursWorked
            })}
            disabled={!row.employee_id || !exists}
        />
    )
}

const HourlyRateCell = ({
    params,
    handleChangeHourlyRate,
    currencySymbol,
    productionEmployees
}) => {
    const { row, value } = params;

    const [hourlyRate, setHourlyRate] = useState(value);
    const [debouncedHourlyRate] = useDebounce(hourlyRate, 250);
    const [exists, setExists] = useState(false);

    useEffect(() => {
        setHourlyRate(value);
    }, [value])

    useEffect(() => {
        if(debouncedHourlyRate && isFinite(debouncedHourlyRate)) {
            handleChangeHourlyRate(row.id, debouncedHourlyRate);
        } else {
            handleChangeHourlyRate(row.id, 0);
        }
    }, [debouncedHourlyRate])

    useEffect(() => {
        if (productionEmployees.length >= 0) {
            if(row.employee_id !== '') {
                const target = productionEmployees?.find((pe) => pe.id === row.employee_id);

                if(target) {
                    setExists(true);
                } else {
                    setExists(false);
                }
            } else {
                setExists(true);
            }
        }
    }, [productionEmployees])

    return (
        <div className='w-full flex items-center'>
            <span style={{lineHeight: '32px', padding: '4px 0 5px', color: (!row.employee_id || !exists) && 'rgba(0, 0, 0, 0.38)'}}>{currencySymbol}</span>
            <TextField
                type="text"
                variant='standard'
                className='w-full mb-5 px-0 pt-0'
                value={hourlyRate}
                onChange={(e) => handleNumber({
                    event: e,
                    setter: setHourlyRate,
                    precision: 2
                })}
                onBlur={(e) => handleZero({
                    event: e,
                    setter: setHourlyRate
                })}
                onFocus={(e) => deleteZero({
                    event: e,
                    setter: setHourlyRate,
                    value: hourlyRate
                })}
                disabled={!row.employee_id || !exists}
            />
        </div>
    )
}

const TotalCell = ({
    params,
    currencySymbol
}) => {
    const { row } = params;

    const [total, setTotal] = useState(0);

    useEffect(() => {
        try {
            setTotal(round(row.hourly_rate * row.hours_worked, 2));
        } catch (e) {
            console.error(e);
            setTotal(0);
        }
    }, [row.hourly_rate, row.hours_worked])

    return (
        <>
            {currencySymbol}
            {total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </>
    )
}

const ActionsCell = ({
    params,
    hourlyRates,
    setHourlyRates
}) => {
    const { t } = useTranslation()

    const { row } = params;

    const handleDeleteHourlyRate = async () => {
        const target = hourlyRates.filter((hr) => hr.id !== row.id);
        setHourlyRates(target);
    }

     return (
        <div className='flex justify-between'>
            <Tooltip
                disableInteractive
                title={t('delete')}
                placement='bottom'
            >
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <span
                        style={{ cursor: 'pointer' }}
                        className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"
                        onClick={handleDeleteHourlyRate}
                    >
                        <i className="fa-solid fa-trash"></i>
                    </span>
                </div>
            </Tooltip>
        </div>
    )
}
